import { defineStore } from 'pinia';
import type { RouteParamsRaw } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';
import { ref, watch } from 'vue';

import type { Landing } from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';

import { TripPlannerPage } from '../models/navigation';
import { TemplateIds } from '../models';

import { useRecipeStore } from './recipe/recipeStore';

type NavigationIndex = { stayOnlyIndex: number } | { ingredientIndex: number };

export const useRouterStore = defineStore('RouterStore', () => {
  const router = useRouter();
  const route = useRoute();
  const recipeStore = useRecipeStore();

  // State
  const isNavigating = ref(false);
  const landingRoute = ref<TripPlannerPage>();
  const isInitialLoad = ref<boolean>(true);
  const landing = ref<Landing | undefined>(undefined);
  const previousTripPlannerPage = ref<TripPlannerPage | undefined>(undefined);
  const pendingHotelSelectionStayOnlyIndex = ref<number>();
  const shouldHideHotelSelectionChromeOnce = ref<boolean>(false);

  // Getters - functions
  function getCurrentTripPlannerView(): TripPlannerPage | undefined {
    return Object.values(TripPlannerPage).find(
      (tripPlannerPage) => tripPlannerPage === route.name
    );
  }

  function getTripOverviewUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/overview${queryParams}`;
  }

  function getHotelSelectionUrl(navigationIndex: NavigationIndex): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > getHotelSelectionUrl > stayOnlyIndex'
    );

    return `/travel/plan/hotel-selection/h${stayOnlyIndex}${queryParams}`;
  }

  function getHotelDetailsUrlInHotelSelection(
    navigationIndex: NavigationIndex,
    hotelCode: string
  ): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > getHotelDetailsUrlInHotelSelection > stayOnlyIndex'
    );
    return `/travel/plan/hotel-selection/h${stayOnlyIndex}/hotel-details/${hotelCode}${queryParams}`;
  }

  function getHotelDetailsUrlInTripOverview(
    navigationIndex: NavigationIndex,
    hotelCode: string
  ): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > getHotelDetailsUrlInTripOverview > stayOnlyIndex'
    );
    return `/travel/plan/overview/h${stayOnlyIndex}/hotel-details/${hotelCode}${queryParams}`;
  }

  function getActivityDetailsUrlInTripOverview(
    navigationIndex: NavigationIndex,
    hotelCode: string
  ): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > getActivityDetailsUrlInTripOverview > stayOnlyIndex'
    );
    return `/travel/plan/overview/h${stayOnlyIndex}/activity-details/${hotelCode}${queryParams}`;
  }

  function getFlightListUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/flights${queryParams}`;
  }

  function getFerryListUrl(): string {
    const queryParams = window.location.search;
    return `/travel/plan/ferry${queryParams}`;
  }

  function getActivitySelectionUrl(navigationIndex: NavigationIndex): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    return `/travel/plan/activity-selection/h${stayOnlyIndex}${queryParams}`;
  }

  function getActivityDetailsUrlInActivitySelection(
    navigationIndex: NavigationIndex,
    activityCode: string
  ): string {
    const queryParams = window.location.search;
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    return `/travel/plan/activity-selection/h${stayOnlyIndex}/activity-details/${activityCode}${queryParams}`;
  }

  // Actions
  function setLanding(param: Landing): void {
    landing.value = param;
  }

  function setLandingRoute(): void {
    landingRoute.value = getCurrentTripPlannerView();
  }

  function setPendingHotelSelectionStayOnlyIndex(stayOnlyIndex: number): void {
    pendingHotelSelectionStayOnlyIndex.value = stayOnlyIndex;
  }

  function consumePendingHotelSelectionStayOnlyIndex(): number | undefined {
    const stayOnlyIndex = pendingHotelSelectionStayOnlyIndex.value;
    pendingHotelSelectionStayOnlyIndex.value = undefined;
    return stayOnlyIndex;
  }

  function setShouldHideHotelSelectionChromeOnce(): void {
    shouldHideHotelSelectionChromeOnce.value = true;
  }

  function consumeShouldHideHotelSelectionChromeOnce(): boolean {
    const shouldHide = shouldHideHotelSelectionChromeOnce.value;
    shouldHideHotelSelectionChromeOnce.value = false;
    return shouldHide;
  }

  async function navigateToBuildYourTrip(): Promise<void> {
    await navigate({ name: TripPlannerPage.BuildYourTrip });
  }

  async function navigateToTripOverview(): Promise<void> {
    await navigate({ name: TripPlannerPage.TripOverview });
  }

  async function navigateToCarTrip(): Promise<void> {
    await navigate({ name: TripPlannerPage.CarTrip });
  }

  async function navigateToFlightsList(): Promise<void> {
    await navigate({ name: TripPlannerPage.FlightList });
  }

  async function navigateToFerryList(): Promise<void> {
    await navigate({ name: TripPlannerPage.FerryList });
  }

  async function navigateToHotelSelection(
    navigationIndex: NavigationIndex
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToHotelSelection > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.HotelSelection,
      params: { ingredientIndex: `h${stayOnlyIndex}` },
    });
  }

  async function navigateToActivitySelection(
    navigationIndex: NavigationIndex
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToActivitySelection > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.ActivitySelection,
      params: { ingredientIndex: `h${stayOnlyIndex}` },
    });
  }

  async function navigateToTripOverviewPrint(): Promise<void> {
    await navigate({
      name: TripPlannerPage.TripOverviewPrint,
    });
  }

  async function navigateToHotelDetailsInHotelSelection(
    navigationIndex: NavigationIndex,
    hotelCode: string
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToHotelDetailsInHotelSelection > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.HotelDetailsInHotelSelection,
      params: { ingredientIndex: `h${stayOnlyIndex}`, hotelCode },
    });
  }

  async function navigateToHotelDetailsInTripOverview(
    navigationIndex: NavigationIndex,
    hotelCode: string,
    scrollToAvailability?: boolean
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToHotelDetailsInTripOverview > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.HotelDetailsInTripOverview,
      params: { ingredientIndex: `h${stayOnlyIndex}`, hotelCode },
      hash: scrollToAvailability
        ? `#${TemplateIds.HotelDetailsStays}`
        : undefined,
    });
  }

  async function navigateToActivityDetailsInInTripOverview(
    navigationIndex: NavigationIndex,
    activityCode: string
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToActivityDetailsInInTripOverview > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.ActivityDetailsInTripOverview,
      params: { ingredientIndex: `h${stayOnlyIndex}`, activityCode },
    });
  }

  async function navigateToActivityDetailsInActivitySelection(
    navigationIndex: NavigationIndex,
    activityCode: string
  ): Promise<void> {
    const stayOnlyIndex = getStayOnlyIndex(navigationIndex);

    assertValue(
      stayOnlyIndex,
      'routerStore > navigateToActivityDetailsInActivitySelection > stayOnlyIndex'
    );

    await navigate({
      name: TripPlannerPage.ActivityDetailsInActivitySelection,
      params: { ingredientIndex: `h${stayOnlyIndex}`, activityCode },
    });
  }

  async function navigate({
    name,
    params,
    hash,
  }: {
    name: TripPlannerPage;
    params?: RouteParamsRaw;
    hash?: string;
  }): Promise<void> {
    await afterNavigation();
    await router.push({
      name,
      params,
      query: route.query,
      ...(hash ? { hash } : {}),
    });
  }

  async function afterNavigation(): Promise<void> {
    return new Promise((resolve) => {
      if (!isNavigating.value) {
        resolve();
      } else {
        watch(isNavigating, () => resolve(), { once: true });
      }
    });
  }

  function getStayOnlyIndex(
    navigationIndex: NavigationIndex
  ): number | undefined {
    return 'stayOnlyIndex' in navigationIndex
      ? navigationIndex.stayOnlyIndex
      : recipeStore.mapRecipeIndexToStayOnlyIndex(
          navigationIndex.ingredientIndex
        );
  }

  return {
    // State
    isInitialLoad,
    isNavigating,
    landing,
    landingRoute,
    previousTripPlannerPage,

    // Getters - functions
    getCurrentTripPlannerView,
    getTripOverviewUrl,
    getHotelSelectionUrl,
    getHotelDetailsUrlInHotelSelection,
    getActivityDetailsUrlInActivitySelection,
    getFlightListUrl,
    getFerryListUrl,
    getActivitySelectionUrl,
    getHotelDetailsUrlInTripOverview,
    getActivityDetailsUrlInTripOverview,

    // Actions
    navigateToBuildYourTrip,
    navigateToActivitySelection,
    navigateToFerryList,
    navigateToFlightsList,
    navigateToHotelSelection,
    navigateToTripOverview,
    navigateToCarTrip,
    navigateToTripOverviewPrint,
    navigateToHotelDetailsInHotelSelection,
    navigateToActivityDetailsInActivitySelection,
    navigateToActivityDetailsInInTripOverview,
    navigateToHotelDetailsInTripOverview,
    setLanding,
    setLandingRoute,
    setPendingHotelSelectionStayOnlyIndex,
    consumePendingHotelSelectionStayOnlyIndex,
    setShouldHideHotelSelectionChromeOnce,
    consumeShouldHideHotelSelectionChromeOnce,
  };
});
